// Rooms list (legacy window/multiplayer.js) and the multiplayer result (window/multiplayerStatsWin.js).
import { useEffect, useState } from 'react';
import { env } from '../env';
import { t } from '../i18n';
import { flagUrl, mpStore, useMultiplayerState, type MpResultPlayer } from '../multiplayer/store';
import { backSrc, sharpFlag } from '../options/assets';
import { uiStore, useUiState, type WindowInstance } from '../store/uiStore';
import '../fonts/saloon.css'; // the saloon type of the titles and the points
import type React from 'react';
import { SuitIcon } from '../hearts/suits';
import { QuickSay, SayBubble } from '../online/QuickSay';
import { AdSlot } from './AdSlot';
import { LobbyView, WaitingRoom, onlineName } from './MultiplayerLobby';
import { useFitWindow } from './useFitWindow';
import { WindowFrame } from './WindowFrame';
import { formatWinTime } from './WinWindow';

const pad = (n: number) => String(n).padStart(2, '0');
const mmss = (s: number) => `${pad(Math.floor(s / 60))}:${pad(s % 60)}`;
export function MultiplayerWindow({ instance }: { instance: WindowInstance }) {
  const mp = useMultiplayerState();
  const fixed = env.appTarget === 'multiplayer';
  const cd = mp.countdown;
  if (cd) {
    // The 10 s before the deal (owner 2026-10-29: the text over the rooms list was "brzydkie"): a card of its own in the
    // cream look of the other multiplayer windows, the seconds big, the two players face to face, the rules in short.
    return (
      <WindowFrame name="multiplayer" className="mp_cream mp_countdown" header={t('menuMultiplayerButton')} overlay={!fixed} closable={false} minimizable={false} closing={instance.closing} minimized={instance.minimized} onClose={() => mpStore().close()}>
        <CreamBackdrop />
        <div className="tab_contents">
          <div className="mp_cont">
            <div className="mp_title">{t('multiplayer.countdown-title')}</div>
            <div className="mp_count" key={cd.sec} aria-live="polite">{cd.sec}</div>
            <Scoreboard
              compact
              sides={[
                { name: mpStore().myName(), country_code: mpStore().myFlag(), score: 0, moves: 0, me: true },
                { name: cd.opponent?.name ?? '...', country_code: cd.opponent?.country_code, score: 0, moves: 0 },
              ]}
            />
            <div className="mp_game">
              <b>{onlineName(cd.game)}</b> · {cd.minutes} min
            </div>
            {/* Kulki (owner 2026-11-03: "Kulki mają stary tekst pasujący do pasjansa"): its own three lines; the card
                games have no countdown (their table starts at once) */}
            <ul className="mp_rules">
              {(cd.game === 'kulki' ? ['same-deal-kulki', 'win-kulki', 'done-kulki'] : ['same-deal', 'win', 'done']).map((k) => (
                <li key={k}>{t(`multiplayer.rule-${k}`)}</li>
              ))}
            </ul>
          </div>
          <div className="rules_buttons">
            <input type="button" value={t('multiplayer.leave-button')} onClick={() => mpStore().leaveRoom()} />
          </div>
        </div>
      </WindowFrame>
    );
  }
  // The lobby (step 2, protocol v2): the loading sign until the server has welcomed us, then the lobby itself
  const lobby = mp.lobby;
  // my card table waits: the waiting room (who is there, who wants to start) in the same window
  const room = mp.table && mp.table.state === 'waiting' && mpStore().isCards(mp.table.game) ? mp.table : null;
  const friend = !room && mp.table?.private && mp.table.state === 'waiting' && mp.table.code ? { code: mp.table.code, link: mpStore().friendLink(mp.table.code, mp.table.variant ?? mp.table.variants[0]), variant: mp.table.variant ?? mp.table.variants[0] ?? '' } : undefined;
  // the lobby in the Games window's look (variant A of the owner's prototype round, 2026-10-29): the class `mp_games`
  // shares the Games window's own rules (windows.css), not its name, so tests and code never take one for the other
  return (
    <WindowFrame name="multiplayer" className="mp_games" header={t('lobby.header')} overlay={!fixed} closable={!fixed} minimizable={!fixed} closing={instance.closing} minimized={instance.minimized} onClose={() => mpStore().close()}>
      {mp.status !== 'ready' || !lobby ? (
        // the game's own loading sign (app/Indicator.tsx: the turning ring of suits), larger, in the middle of the
        // window; the suits are our SVG icons (owner: suits are never text), so the element's text stays the words
        <div className="games_wrapper">
          <div className="multi_loading" role="status">
            <span className="multi_loading_spin" aria-hidden="true">
              {(['S', 'H', 'C', 'D'] as const).map((s, i) => (
                <span key={s} className={`multi_loading_suit multi_loading_suit--${i}`}>
                  <SuitIcon s={s} />
                </span>
              ))}
            </span>
            <span className="multi_loading_text">{t(mp.status === 'lost' ? 'lobby.reconnecting' : 'multiplayer.loading-room')}</span>
          </div>
        </div>
      ) : (
        room ? (
          <WaitingRoom
            table={room}
            me={mpStore().mySeat()}
            minutes={mp.catalog.flatMap((g) => g.variants).find((v) => v.id === (room.variant ?? room.variants[0]))?.minutes ?? 0}
            link={room.private && room.code ? mpStore().friendLink(room.code, room.variant ?? room.variants[0]) : undefined}
          />
        ) : (
          <LobbyView catalog={mp.catalog} lobby={lobby} error={mp.error} friend={friend} mine={!room && !friend && mp.table?.state === 'waiting' && !mp.table.private ? mp.table : undefined} />
        )
      )}
    </WindowFrame>
  );
}

/** A HUD caption without its colon ("Punkty:" -> "Punkty"). */
const caption = (key: string) => t(key).replace(/[:：]\s*$/, '').trim();

export interface BoardSide {
  name: string;
  country_code?: string | undefined;
  score: number;
  moves: number;
  time?: number | undefined;
  me?: boolean | undefined;
  /** The small mark under the numbers: still playing, done, left, or the winner's crown. */
  badge?: 'playing' | 'done' | 'left' | 'winner' | undefined;
  /** The result window: the seat whose quick messages pop out of this side. */
  seat?: number | undefined;
}

const Crown = () => (
  <svg className="mp_crown" viewBox="0 0 24 16" aria-hidden="true">
    <path d="M2 14 L1 3 L7 8 L12 1 L17 8 L23 3 L22 14 Z" />
  </svg>
);

/** The two players side by side, the points big (the multiplayer windows of step 0b, owner 2026-10-29 "zrób te okna
 *  ładniejsze"): the leave choice, the wait after "I'm done" and the result share it. */
export function Scoreboard({ sides, compact = false, flat = false }: { sides: [BoardSide, BoardSide]; compact?: boolean; flat?: boolean }) {
  const side = (p: BoardSide) => (
    <div className={`mp_side${p.me ? ' mp_me' : ''}${p.badge === 'winner' ? ' mp_winner' : ''}`}>
      {p.seat === undefined ? null : <SayBubble seat={p.seat} />}
      {p.badge === 'winner' ? <Crown /> : null}
      <div className="mp_who">
        {p.country_code ? <img src={flagUrl(p.country_code)} alt="" /> : null}
        <span className="mp_name" title={p.name}>{p.name}</span>
      </div>
      <div className="mp_tag">{p.me ? t('multiplayer.you') : '\u00a0'}</div>
      {compact ? null : (
        <>
      <div className="mp_points">{p.score}</div>
      <div className="mp_caption">{caption('winScoresHead')}</div>
      <div className="mp_meta">
        <span className="mp_moves">
          {caption('winMovesHead')}: <b>{p.moves}</b>
        </span>
        {p.time === undefined ? null : (
          <span className="mp_time">
            {caption('winTimeHead')}: <b>{formatWinTime(p.time)}</b>
          </span>
        )}
      </div>
        </>
      )}
      {p.badge && p.badge !== 'winner' ? (
        <div className={`mp_badge mp_badge_${p.badge}`}>
          {t(`multiplayer.badge-${p.badge}`)}
          {p.badge === 'playing' ? <span className="mp_dots" aria-hidden="true"><i>.</i><i>.</i><i>.</i></span> : null}
        </div>
      ) : null}
    </div>
  );
  return (
    <div className={`mp_board${compact ? ' mp_board_compact' : ''}${flat ? ' mp_board_flat' : ''}`}>
      {side(sides[0])}
      <div className="mp_vs" aria-hidden="true">vs</div>
      {side(sides[1])}
    </div>
  );
}

/** The cream backdrop of the Pause and No-moves windows (card backs in a faint grid). */
export function CreamBackdrop() {
  const skin = useUiState().options.skin;
  return <div className="pause_backdrop" aria-hidden="true" style={{ '--pause-back': `url("${backSrc(skin.cardBackType)}")` } as React.CSSProperties} />;
}

export function MultiplayerResultWindow({ instance }: { instance: WindowInstance }) {
  const mp = useMultiplayerState();
  const result = mp.result;
  const [k, setK] = useState(0); // the count-up, 0 to 1
  // a low screen: the window drops its extras step by step until it fits (useFitWindow.ts, windows.css data-fit-*)
  useFitWindow('.lbx_window.lbx_multiplayer_win', 4, !!result);
  useEffect(() => {
    if (!result) return;
    let raf = 0;
    const t0 = performance.now();
    const step = (now: number) => {
      const j = Math.min(1, (now - t0) / 1200);
      setK(j);
      if (j < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [result]);
  if (!result) return null;
  const [a, b] = result.players;
  // the higher score wins, then fewer moves (legacy multiplayerStatsWin.js); the one who stays wins when the other left
  const byScore = a.score !== b.score ? (a.score > b.score ? 0 : 1) : a.moves !== b.moves ? (a.moves < b.moves ? 0 : 1) : null;
  // the server's word when it gives one (v2: clearing the board wins whatever the scores); the stayer wins a left game
  const winner = result.winner !== undefined ? result.winner : result.left ? result.players.findIndex((p) => p.me) : byScore;
  const meIndex = result.players.findIndex((p) => p.me);
  const title = winner === null ? t('multiplayer.tie') : winner === meIndex ? t('multiplayer.you-win') : t('multiplayer.player-wins', { player: result.players[winner]!.name });
  const ease = (x: number) => (x < 0.5 ? 2 * x * x : -1 + (4 - 2 * x) * x);
  const done = k >= 1;
  const sideOf = (p: MpResultPlayer, i: number): BoardSide => ({
    name: p.name, country_code: p.country_code, me: p.me, time: p.time, moves: p.moves, seat: i,
    score: Math.floor(ease(k) * p.score),
    badge: done && winner === i ? 'winner' : result.left && !p.me ? 'left' : undefined,
  });
  return (
    <WindowFrame name="multiplayer_win" className="mp_cream" header={t('winHeader')} closable={false} closing={instance.closing}>
      <CreamBackdrop />
      <div className="tab_contents">
        <div className="mp_cont">
          <div className={`mp_title${done ? ' mp_title_in' : ''}`}>{done ? title : '\u00a0'}</div>
          <div className="mp_sub">
            {result.left ? <span dangerouslySetInnerHTML={{ __html: t('multiplayer.opponent-left', { player: result.left }) }} /> : <>{t(`win.${result.game_type}`)}</>}
          </div>
          <Scoreboard sides={[sideOf(a, 0), sideOf(b, 1)]} />
          <QuickSay iWon={winner === meIndex} />
          {mp.rematch.them && !mp.rematch.me ? <div className="mp_note">{t('multiplayer.rematch-asked')}</div> : null}
          {mp.rematch.gone && !result.left ? <div className="mp_note">{t('multiplayer.rematch-gone')}</div> : null}
        </div>
        <AdSlot slot={2} />
        <div className="win_buttons">
          {/* Lobby reloads the page (the ads refresh); New opponent and Rematch go on at once (owner 2026-10-29) */}
          <input type="button" className="mp_btn_secondary" value={t('multiplayer.to-lobby')} onClick={() => mpStore().toLobby()} />
          <input type="button" className="mp_btn_secondary" value={t('multiplayer.new-opponent')} onClick={() => mpStore().newOpponent()} />
          <input
            type="button"
            className={mp.rematch.gone || mp.rematch.me ? 'disabled' : ''}
            disabled={mp.rematch.gone || mp.rematch.me}
            value={t(mp.rematch.me ? 'multiplayer.rematch-waiting' : 'multiplayer.rematch')}
            onClick={() => mpStore().rematch()}
          />
        </div>
      </div>
    </WindowFrame>
  );
}

/** `#footer_multiplayer`: the opponent's live score during a game ("is done" once they pressed I'm done). */
export function OpponentBar() {
  const mp = useMultiplayerState();
  const o = mp.opponent;
  if (!mp.gameStarted || !o) return <div id="footer_multiplayer" hidden />;
  const key = o.done ? 'multiplayer.opponent-done' : 'multiplayer.opponent-stats';
  let html = t(key, { player_name: o.name, flag: sharpFlag(flagUrl(o.country_code)), score: o.score, moves: o.moves });
  // no country known (a guest): no flag, and no empty brackets where it stood (owner 2026-11-02)
  if (!o.country_code) html = html.replace(/\s*\(\s*<img[^>]*>\s*\)/, '').replace(/<img[^>]*>\s*/, '');
  return <div id="footer_multiplayer" className={innerHeight > 750 ? 'bigger' : ''} dangerouslySetInnerHTML={{ __html: html }} />;
}

/** The Leave button during a game (step 0b of .ai/multiplayer-plan.md): "I'm done" keeps the score so far and waits
 *  for the opponent; Leave hands them the win; Keep playing closes. Two big choice cards over the scoreboard. */
export function MultiplayerLeaveWindow({ instance }: { instance: WindowInstance }) {
  const mp = useMultiplayerState();
  const close = () => uiStore().closeWindow('multiplayer_leave');
  const o = mp.opponent;
  const player = o?.name ?? '...';
  const me = mpStore().myStats();
  if (mp.cards) {
    // a card game: no score to give away; a bot plays my seat to the end
    return (
      <WindowFrame name="multiplayer_leave" className="mp_cream" header={t('multiplayer.choice-header')} overlay closing={instance.closing}>
        <CreamBackdrop />
        <div className="tab_contents">
          <div className="mp_cont">
            <div className="mp_help">
              <p>{t('lobby.cards-leave-text')}</p>
            </div>
          </div>
          <div className="rules_buttons">
            <input type="button" className="mp_btn_secondary" value={t('multiplayer.keep-playing')} onClick={close} />
            <input type="button" className="mp_btn_primary mp_leave" value={t('lobby.room.leave')} onClick={() => { close(); mpStore().leaveCards(); }} />
          </div>
        </div>
      </WindowFrame>
    );
  }
  return (
    <WindowFrame name="multiplayer_leave" className="mp_cream" header={t('multiplayer.choice-header')} overlay closing={instance.closing}>
      <CreamBackdrop />
      <div className="tab_contents">
        <div className="mp_cont">
          {/* a flat strip, not cards: nothing here looks like a button except the buttons (owner 2026-10-29: "nie wiem
              co jest przyciskiem, wszystko ma ten sam kształt") */}
          <Scoreboard
            flat
            sides={[
              { name: mpStore().myName(), country_code: mpStore().myFlag(), score: me.score, moves: me.moves, me: true },
              { name: player, country_code: o?.country_code, score: o?.score ?? 0, moves: o?.moves ?? 0, badge: o?.done ? 'done' : 'playing' },
            ]}
          />
          <div className="mp_help">
            <p>
              <b>{t('multiplayer.done-button')}</b>: {t('multiplayer.choice-done-text', { player })}
            </p>
            <p>
              <b>{t('multiplayer.leave-button')}</b>: {t('multiplayer.choice-leave-text', { player })}
            </p>
          </div>
        </div>
        <div className="rules_buttons">
          <input type="button" className="mp_btn_secondary" value={t('multiplayer.keep-playing')} onClick={close} />
          <input type="button" className="mp_btn_secondary mp_leave" value={t('multiplayer.leave-button')} onClick={() => { close(); mpStore().leaveRoom(); }} />
          <input type="button" className="mp_btn_primary mp_done" value={t('multiplayer.done-button')} onClick={() => { close(); mpStore().done(); }} />
        </div>
      </div>
    </WindowFrame>
  );
}

/** After "I'm done": the scoreboard (mine final, the opponent's live) and the clock, until the result window replaces
 *  it. No close: the board waits under it. Leave stays possible (the opponent then wins). Room for an ad (a long wait). */
export function MultiplayerDoneWindow({ instance }: { instance: WindowInstance }) {
  const mp = useMultiplayerState();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const me = mp.meDone ?? { score: 0, moves: 0, stuck: false };
  const o = mp.opponent;
  const player = o?.name ?? '...';
  const left = mp.endsAt === null ? null : Math.max(0, Math.ceil((mp.endsAt - now) / 1000));
  return (
    <WindowFrame name="multiplayer_done" className="mp_cream" header={t('menuMultiplayerButton')} overlay closable={false} closing={instance.closing}>
      <CreamBackdrop />
      <div className="tab_contents">
        <div className="mp_cont">
          <div className="mp_title mp_title_in">{t(me.stuck ? 'lostGame.title' : 'multiplayer.waiting-title')}</div>
          <Scoreboard
            sides={[
              { name: mpStore().myName(), country_code: mpStore().myFlag(), score: me.score, moves: me.moves, me: true, badge: 'done' },
              { name: player, country_code: o?.country_code, score: o?.score ?? 0, moves: o?.moves ?? 0, badge: o?.done ? 'done' : 'playing' },
            ]}
          />
          {left === null ? null : (
            <div className={`mp_clock${left <= 30 ? ' mp_clock_low' : ''}`}>
              <span className="mp_clock_label">{t('multiplayer.time-left')}</span>
              <span className="mp_clock_time">{mmss(left)}</span>
            </div>
          )}
          {/* the server ends the race at once when the opponent has more points: while this shows, they are behind */}
          <div className="mp_sub">
            {me.stuck ? `${t('multiplayer.stuck-text')} ` : null}
            {t('multiplayer.done-beat', { player })}
          </div>
        </div>
        <AdSlot slot={2} />
        <div className="rules_buttons">
          <input type="button" value={t('multiplayer.leave-button')} onClick={() => mpStore().leaveRoom()} />
        </div>
      </div>
    </WindowFrame>
  );
}

export { uiStore };
